import './MiaOrb.css';

type MiaOrbMood = 'idle' | 'asking' | 'thinking' | 'success';

type MiaOrbProps = {
  size?: number;
  mood?: MiaOrbMood;
};

const centred: React.CSSProperties = {
  position: 'absolute',
  left: '50%',
  top: '50%',
};

/**
 * Mia's breathing-halo mark, ported from the design system's MiaOrb.
 * Pure CSS/DOM — no assets. Decorative: hidden from screen readers.
 */
function MiaOrb({ size = 44, mood = 'idle' }: MiaOrbProps) {
  const isAsking = mood === 'asking';
  const isThinking = mood === 'thinking';
  const isSuccess = mood === 'success';
  const isIdle = !isAsking && !isThinking && !isSuccess;

  const dur = isAsking ? 4.4 : isThinking ? 5 : isSuccess ? 2.8 : 6.5;
  const bw = Math.max(1, size * 0.022);
  const core = Math.round(size * 0.4);
  const ring = Math.round(size * 0.7);
  const ringOuter = Math.round(size * 0.86);
  const glow = Math.round(size * 0.93);
  const arc = Math.round(size * 0.68);
  const glyph = Math.round(core * 0.74);

  const showBreatheRing = isIdle || isAsking;
  const showSmile = isIdle || isAsking || isThinking;

  return (
    <div
      className='mia-orb'
      aria-hidden='true'
      style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}
    >
      <div
        style={{
          ...centred,
          width: glow,
          height: glow,
          borderRadius: '50%',
          background: `radial-gradient(circle, rgba(63,198,173,${isSuccess ? 0.28 : 0.22}), transparent 68%)`,
          transform: 'translate(-50%,-50%)',
          animation: `hGlow ${dur}s ease-in-out infinite`,
        }}
      />
      {isAsking && (
        <div
          style={{
            ...centred,
            width: ringOuter + bw * 2,
            height: ringOuter + bw * 2,
            borderRadius: '50%',
            background:
              'radial-gradient(circle, rgba(79,196,168,0) 52%, rgba(79,196,168,0.5) 70%, rgba(79,196,168,0) 82%)',
            animation: `hRingOuter ${(dur * 1.43).toFixed(2)}s ease-in-out -${(dur * 0.91).toFixed(2)}s infinite`,
          }}
        />
      )}
      {showBreatheRing && (
        <>
          <div
            style={{
              ...centred,
              width: ring,
              height: ring,
              borderRadius: '50%',
              border: `${bw}px solid #4fb89a`,
              animation: `hRing ${(dur * 0.82).toFixed(2)}s ease-in-out -${(dur * 0.37).toFixed(2)}s infinite, hHue ${(dur * 2.4).toFixed(2)}s ease-in-out infinite`,
            }}
          />
          <div
            style={{
              ...centred,
              transform: 'translate(-50%,-50%)',
              width: ring,
              height: ring,
              borderRadius: '50%',
              background:
                'radial-gradient(circle, rgba(193,186,172,0.4), transparent 66%)',
              animation: `hExpand ${(dur * 1.27).toFixed(2)}s ease-out -${(dur * 0.53).toFixed(2)}s infinite`,
            }}
          />
          <div
            style={{
              ...centred,
              transform: 'translate(-50%,-50%)',
              width: ringOuter,
              height: ringOuter,
              borderRadius: '50%',
              border: `${bw}px solid #b8b3a8`,
              animation: `hImplode ${(dur * 1.63).toFixed(2)}s ease-in-out -${(dur * 0.9).toFixed(2)}s infinite, hHue3 ${(dur * 2.1).toFixed(2)}s ease-in-out infinite`,
            }}
          />
        </>
      )}
      {isThinking && (
        <div
          style={{
            ...centred,
            width: arc,
            height: arc,
            borderRadius: '50%',
            border: `${bw}px solid transparent`,
            borderTopColor: '#256956',
            borderRightColor: '#6f9483',
            animation: 'hArc 3.4s linear infinite',
          }}
        />
      )}
      {isSuccess && (
        <div
          style={{
            ...centred,
            width: ring,
            height: ring,
            borderRadius: '50%',
            border: `${bw}px solid #6f9483`,
            animation: 'hExpand 2.8s ease-out infinite',
          }}
        />
      )}
      <div
        style={{
          ...centred,
          width: core,
          height: core,
          borderRadius: '50%',
          background: 'linear-gradient(140deg, #9ee0c6, #c0dbeb 52%, #fecdd3)',
          boxShadow: '0 4px 14px -6px rgba(96,150,170,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          animation: isSuccess
            ? 'hSettle 2.8s cubic-bezier(.3,.9,.3,1.2) infinite, oBlob 7s ease-in-out infinite'
            : `hCore ${dur}s ease-in-out infinite, oBlob 7s ease-in-out infinite`,
        }}
      >
        {showSmile && (
          <svg
            width={glyph}
            height={glyph}
            viewBox='0 0 32 32'
            fill='none'
            stroke='#1f5042'
            strokeWidth='2.4'
            strokeLinecap='round'
            strokeLinejoin='round'
          >
            <circle
              cx='11'
              cy='13'
              r='1.7'
              fill='#1f5042'
              stroke='none'
              style={{
                transformBox: 'fill-box',
                transformOrigin: 'center',
                animation: 'oBlink 6s ease-in-out infinite',
              }}
            />
            <circle
              cx='21'
              cy='13'
              r='1.7'
              fill='#1f5042'
              stroke='none'
              style={{
                transformBox: 'fill-box',
                transformOrigin: 'center',
                animation: 'oWink 6s ease-in-out infinite',
              }}
            />
            <path d='M10.5 19q5.5 4.2 11 0' />
          </svg>
        )}
        {isSuccess && (
          <svg
            width={glyph}
            height={glyph}
            viewBox='0 0 24 24'
            fill='none'
            stroke='#1f5042'
            strokeWidth='3'
            strokeLinecap='round'
            strokeLinejoin='round'
          >
            <path d='M20 6L9 17l-5-5' />
          </svg>
        )}
      </div>
    </div>
  );
}

export default MiaOrb;
